import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { Component, ErrorInfo, ReactNode, useEffect } from "react";

import { COMMON_COPY } from "copy/common";
import { useCopy } from "i18n/useCopy";

import { logError } from "../../telemetry/appInsights";
import { isAuthenticationError, isChunkLoadError } from "../../types/errors/ApiErrors";

/**
 * Props for the ErrorBoundary component
 */
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * State for the ErrorBoundary component
 */
interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Component that handles redirecting to login when session expires.
 * Uses Auth0's loginWithRedirect with the current URL as the return path.
 */
const AuthRedirectHandler: React.FC = () => {
  const copy = useCopy();
  const { loginWithRedirect } = useAuth0();

  useEffect(() => {
    const redirectToLogin = async () => {
      console.log("Session expired, redirecting to login...");
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    };
    redirectToLogin();
  }, [loginWithRedirect]);

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {copy.text(COMMON_COPY.session.expiredRedirecting)}
      </Typography>
    </Box>
  );
};

/**
 * Prompt shown when a lazily loaded chunk is gone because a new build was deployed.
 */
const NewVersionFallback: React.FC<{ onReload: () => void }> = ({ onReload }) => {
  const copy = useCopy();

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="primary" gutterBottom>
          {copy.text(COMMON_COPY.errorBoundary.newVersion.title)}
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          {copy.text(COMMON_COPY.errorBoundary.newVersion.description)}
        </Typography>

        <Typography variant="body2" color="text.disabled" paragraph>
          {copy.text(COMMON_COPY.errorBoundary.newVersion.explanation)}
        </Typography>

        <Box sx={{ mt: 3 }}>
          <Button variant="contained" color="primary" onClick={onReload} size="large">
            {copy.text(COMMON_COPY.errorBoundary.reloadPage)}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * Generic crash screen, with the error and component stack in development builds.
 */
const CrashFallback: React.FC<{
  error?: Error;
  errorInfo?: ErrorInfo;
  onReset: () => void;
  onReload: () => void;
}> = ({ error, errorInfo, onReset, onReload }) => {
  const copy = useCopy();

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 600,
          textAlign: "center",
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          {copy.text(COMMON_COPY.errorBoundary.crash.title)}
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          {copy.text(COMMON_COPY.errorBoundary.crash.description)}
        </Typography>

        {process.env.NODE_ENV === "development" && error && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              textAlign: "left",
            }}
          >
            <Typography variant="h6" gutterBottom>
              {copy.text(COMMON_COPY.errorBoundary.crash.developmentDetailsHeading)}
            </Typography>
            <Typography variant="body2" component="pre" sx={{ overflow: "auto" }}>
              {error.toString()}
            </Typography>
            {errorInfo && (
              <Typography variant="body2" component="pre" sx={{ overflow: "auto", mt: 1 }}>
                {errorInfo.componentStack}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="contained" color="primary" onClick={onReset}>
            {copy.text(COMMON_COPY.errorBoundary.tryAgain)}
          </Button>
          <Button variant="outlined" onClick={onReload}>
            {copy.text(COMMON_COPY.errorBoundary.reloadPage)}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });

    // Don't log authentication errors as they're expected when session expires
    if (isAuthenticationError(error)) {
      console.log("Authentication error caught in ErrorBoundary, will redirect to login");
      return;
    }

    // Don't log chunk load errors as alarming - they're expected during deployments
    if (isChunkLoadError(error)) {
      console.log("Chunk load error caught in ErrorBoundary, will prompt user to reload");
      return;
    }

    // Log error to Application Insights
    logError("ErrorBoundary caught an error", error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: "ErrorBoundary",
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Handle authentication errors by redirecting to login
      if (this.state.error && isAuthenticationError(this.state.error)) {
        return <AuthRedirectHandler />;
      }

      // Handle chunk load errors with a version update prompt
      if (this.state.error && isChunkLoadError(this.state.error)) {
        return <NewVersionFallback onReload={this.handleReload} />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <CrashFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
